/**
 * ARAM Platform - Route Permission Guard
 * Core Desarrollos S.A.S - Copyright 2024-2026
 *
 * Wraps routes that require specific permissions.
 * Renders children if the user has the required permission(s),
 * otherwise redirects to /401 (Unauthorized page).
 *
 * @example
 * // Single permission
 * <RequirePermission permission="fleet:write">
 *   <VehiclesPage />
 * </RequirePermission>
 *
 * // Any of several permissions (OR logic)
 * <RequirePermission anyOf={['orders:read', 'orders:write']}>
 *   <OrdersPage />
 * </RequirePermission>
 *
 * // Superuser-only
 * <RequirePermission superuserOnly>
 *   <SettingsPage />
 * </RequirePermission>
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'

export interface RequirePermissionProps {
  /** Single permission to check (exact match) */
  permission?: string
  /** Pass if user has ANY of these permissions (OR logic) */
  anyOf?: string[]
  /** Only allow superusers */
  superuserOnly?: boolean
  /** Where to redirect on denied (default: /401) */
  redirectTo?: string
  children: React.ReactNode
}

export function RequirePermission({
  permission,
  anyOf,
  superuserOnly,
  redirectTo = '/401',
  children,
}: RequirePermissionProps) {
  const { user, hasPermission, hasAnyPermission } = useAuthStore()
  const location = useLocation()

  // Not logged in — DashboardLayout already handles this, but just in case
  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  // Superusers bypass all permission checks
  if (user.is_superuser) {
    return <>{children}</>
  }

  // Superuser-only route
  if (superuserOnly) {
    return <Navigate to={redirectTo} replace />
  }

  // Single permission check
  if (permission && !hasPermission(permission)) {
    return <Navigate to={redirectTo} replace />
  }

  // Any-of check
  if (anyOf && anyOf.length > 0 && !hasAnyPermission(anyOf)) {
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}
